import numeral from 'numeral';
import { PapiDeviation } from '@wix/da-papi-types';
import { parseIsoDateOrTimestamp } from '@wix/da-i18next/pkg/time/functions';

export function getIsAdoptableOfferType(deviation: PapiDeviation): boolean {
  return !!deviation.adoptable?.offers;
}

export function getIsAdoptableFixedPriceType(
  deviation: PapiDeviation
): boolean {
  return !!deviation.adoptable?.fixedPrice;
}

export function getAllowOffers(deviation: PapiDeviation): boolean {
  return getIsOnSale(deviation) && getIsAdoptableOfferType(deviation);
}

export function getHasBuyItNow(deviation: PapiDeviation): boolean {
  return getIsOnSale(deviation) && getIsAdoptableFixedPriceType(deviation);
}

export function getCanSeeMinPrice(deviation: PapiDeviation): boolean {
  const isOnSale = getIsOnSale(deviation);
  const isAdoptableFixedPriceType = getIsAdoptableFixedPriceType(deviation);
  const isAdoptableOfferType = getIsAdoptableOfferType(deviation);
  /**
   * Here's a tiny truth table, for the canSeeMinPrice logic
   * | isAcceptingOffers      | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ |
   * | hasFixedPrice          | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ |   |   |   |   |   |   |   |   |
   * | hasOfferEndTime        | ✓ | ✓ | ✓ | ✓ |   |   |   |   | ✓ | ✓ | ✓ | ✓ |   |   |   |   |
   * | hasOfferEndTimeExpired | ✓ | ✓ |   |   | ✓ | ✓ |   |   | ✓ | ✓ |   |   | ✓ | ✓ |   |   |
   * | hasOffers              | ✓ |   | ✓ |   | ✓ |   | ✓ |   | ✓ |   | ✓ |   | ✓ |   | ✓ |   |
   * |-------------------+---+---+---+---+---+---+---+---+---+---+---+---+---+---+---+---+
   * | canSeeMinPrice         |   |   |   | ✓ |   |   |   |   |   |   |   | ✓ |   |   |   | ✓ |
   */

  if (getIsInEarlyAccess(deviation) && getIsOwner(deviation)) {
    return true;
  }

  if (getIsInEarlyAccess(deviation) && !getIsSupporter(deviation)) {
    return false;
  }

  const isAcceptingOffers = isOnSale && isAdoptableOfferType;
  if (!isAcceptingOffers) {
    return false;
  }

  const isFixedPrice = isOnSale && isAdoptableFixedPriceType;
  const hasOffers = getHasOffers(deviation);
  const hasOfferEndTime = !!getOfferCountdownEndTime(deviation);
  const hasOfferEndTimeExpired = !getIsBeforeOfferEndTime(deviation);

  if (isFixedPrice) {
    return hasOfferEndTime && !hasOfferEndTimeExpired && !hasOffers;
  }

  if (hasOfferEndTime) {
    return !hasOfferEndTimeExpired && !hasOffers;
  }

  if (!hasOfferEndTimeExpired) {
    return !hasOffers;
  }

  return false;
}

export function getEarlyAccess(deviation: PapiDeviation) {
  const defaults = {
    isInEarlyAccess: false,
  };

  return { ...defaults, ...(deviation.extended?.adoptable?.earlyAccess ?? {}) };
}

export function getIsInEarlyAccess(deviation: PapiDeviation) {
  return !!deviation.extended?.adoptable?.earlyAccess?.isInEarlyAccess;
}

export function getIsSupporter(deviation: PapiDeviation) {
  return !!deviation.extended?.adoptable?.earlyAccess?.isSupporter;
}

export function getIsOwner(deviation: PapiDeviation) {
  return !!deviation.extended?.adoptable?.isOwner;
}

export function getEarlyAccessCountdownEndTime(
  deviation: PapiDeviation
): string | undefined {
  return deviation.extended?.adoptable?.earlyAccess?.earlyAccessEndTime;
}

export function getCanSeeEarlyAccessCountdown(
  deviation: PapiDeviation
): boolean {
  return (
    !!getEarlyAccessCountdownEndTime(deviation) &&
    !!getIsInEarlyAccess(deviation)
  );
}

export function getOfferCountdownEndTime(
  deviation: PapiDeviation
): string | undefined {
  return (
    deviation.extended?.adoptable?.offers?.offerEndTime ??
    deviation.adoptable?.offers?.offerEndTime
  );
}

export function getCanSeeOfferCountdown(deviation: PapiDeviation): boolean {
  return (
    !!getOfferCountdownEndTime(deviation) && !getIsInEarlyAccess(deviation)
  );
}

const OFFER_END_TIME_ADJUST = 1;

export function getIsBeforeOfferEndTime(deviation: PapiDeviation): boolean {
  const offerEndTimeString = getOfferCountdownEndTime(deviation);
  if (!offerEndTimeString) {
    // there was never an offer window to begin with? huh.
    return true;
  }
  const offerEndTime = parseIsoDateOrTimestamp(offerEndTimeString);
  // client-side coordinated timing is hardly ever a good idea
  const almostNow = new Date();
  almostNow.setSeconds(almostNow.getSeconds() + OFFER_END_TIME_ADJUST);
  return offerEndTime && offerEndTime >= almostNow;
}

export function getHasOffers(deviation: PapiDeviation): boolean {
  return (
    !!deviation.extended?.adoptable?.offers?.numOffers &&
    deviation.extended?.adoptable?.offers?.numOffers > 0
  );
}

export function getIsPending(deviation: PapiDeviation): boolean {
  return deviation.adoptable?.status === 'pending';
}

export function getIsOnSale(deviation: PapiDeviation): boolean {
  return deviation.adoptable?.status === 'onsale';
}

export function getIsOnResale(deviation: PapiDeviation): boolean {
  return (
    deviation.adoptable?.status === 'onsale' &&
    deviation.author?.useridUuid !== deviation.adoptable?.owner?.useridUuid
  );
}

export function getIsNotOnSale(deviation: PapiDeviation): boolean {
  return deviation.adoptable?.status === 'not_onsale';
}

export function getIsAdopted(deviation: PapiDeviation): boolean {
  return deviation.adoptable?.status === 'sold';
}

export function getHasOwnOffer(deviation: PapiDeviation): boolean {
  return Boolean(deviation.extended?.adoptable?.offers?.ownOffer);
}

export function getHasRejectedOffer(deviation: PapiDeviation): boolean {
  return (
    !!deviation.extended?.adoptable?.offers?.ownOffer?.status &&
    deviation.extended?.adoptable?.offers?.ownOffer?.status === 'rejected'
  );
}

export function getPrice(deviation: PapiDeviation) {
  return numeral(deviation.extended?.adoptable?.fixedPrice?.fixedPrice).format(
    '0,0'
  );
}

export function getDeviationOwner(deviation: PapiDeviation) {
  return getIsAdopted(deviation) ? deviation.adoptable?.owner : undefined;
}

export function getSaleCount(deviation: PapiDeviation) {
  return deviation.extended?.adoptable?.saleCount ?? 0;
}
